import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

export type ColorEnum = 'CORAL' | 'TEAL' | 'GREEN' | 'LILAC' | 'PURPLE';

export interface Category {
  id: string;
  name: string;
  color: ColorEnum;
  sort: number;
  archivedAt: string | null;
}

export interface CreateCategoryBody {
  name: string;
  color: ColorEnum;
  sort?: number;
}

export interface PatchCategoryBody {
  name?: string;
  color?: ColorEnum;
}

function reorderArray<T>(
  items: T[] | undefined,
  idsInOrder: string[],
  getId: (t: T) => string,
): T[] | undefined {
  if (!items) return items;
  const byId = new Map(items.map((it) => [getId(it), it]));
  const next: T[] = [];
  for (const id of idsInOrder) {
    const found = byId.get(id);
    if (found) next.push(found);
  }
  // Append any items not in idsInOrder at the end (defensive).
  for (const it of items) {
    if (!idsInOrder.includes(getId(it))) next.push(it);
  }
  return next;
}

export function useCategories(eventId: string) {
  return useQuery({
    queryKey: ['event', eventId, 'categories'],
    queryFn: () =>
      apiFetch<{ categories: Category[] }>(
        `/api/v1/admin/events/${eventId}/categories`,
      ).then((r) => r.categories),
    enabled: Boolean(eventId),
  });
}

export function useCreateCategory(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCategoryBody) =>
      apiFetch<{ category: Category }>(`/api/v1/admin/events/${eventId}/categories`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.category),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'categories'] });
    },
  });
}

export function usePatchCategory(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchCategoryBody }) =>
      apiFetch<{ category: Category }>(`/api/v1/admin/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.category),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'categories'] });
    },
  });
}

export function useArchiveCategory(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/admin/categories/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'categories'] });
    },
  });
}

export function useReorderCategories(eventId: string) {
  const qc = useQueryClient();
  const key = ['event', eventId, 'categories'] as const;
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch<void>(`/api/v1/admin/events/${eventId}/categories/reorder`, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    onMutate: async (ids: string[]) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Category[]>(key);
      qc.setQueryData<Category[] | undefined>(key, (old) =>
        reorderArray(old, ids, (c) => c.id),
      );
      return { previous };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}
